/**
 * NeuroMent — Design Tokens
 *
 * Fonte única de verdade para identidade visual.
 * Edite aqui; o resto do app deve referenciar estes tokens.
 */

export const brand = {
  // ─── IDENTIDADE ───────────────────────────────────────────────────────────
  name: "NeuroMent",
  tagline: "Aprendizado que se ajusta a quem estuda.",
  taglineShort: "Personalização por perfil.",

  // ─── CORES ────────────────────────────────────────────────────────────────
  color: {
    // Fundos
    bg: {
      canvas: "#050a14",      // fundo principal da app/landing
      footer: "#030711",      // rodapé e superfícies mais profundas
      surface: "#0d1525",     // cards e painéis sobre o canvas
      surfaceHover: "#111d30",
      glass: "rgba(255,255,255,0.04)", // glassmorphism base
    },

    // Primária — teal/cyan (gradiente de marca)
    primary: {
      from: "#2dd4bf",        // teal-400
      mid: "#14b8a6",         // teal-500
      to: "#0891b2",          // cyan-600
      gradient: "linear-gradient(135deg, #2dd4bf 0%, #0891b2 100%)",
      glow: "rgba(20,184,166,0.5)",
    },

    // Texto
    text: {
      primary: "#ffffff",
      secondary: "#cbd5e1",   // slate-300
      muted: "#94a3b8",       // slate-400
      hint: "#64748b",        // slate-500
      accent: "#5eead4",      // teal-300 — links e highlights
    },

    // Bordas
    border: {
      subtle: "rgba(255,255,255,0.08)",
      default: "rgba(255,255,255,0.12)",
      accent: "rgba(45,212,191,0.3)",   // teal-400/30
      strong: "rgba(45,212,191,0.5)",
    },

    // Semânticas (status / feedback)
    semantic: {
      success: "#22c55e",     // green-500
      warning: "#f59e0b",     // amber-500
      error: "#ef4444",       // red-500
      info: "#06b6d4",        // cyan-500
    },

    // Papéis (personas do sistema)
    role: {
      student: { from: "#22d3ee", to: "#38bdf8" },    // cyan→sky
      institutional: { from: "#38bdf8", to: "#3b82f6" }, // sky→blue
      teacher: { from: "#34d399", to: "#14b8a6" },    // emerald→teal
      coordinator: { from: "#a78bfa", to: "#e879f9" },// violet→fuchsia
      admin: { from: "#94a3b8", to: "#71717a" },      // slate→zinc
    },

    // Planos
    plan: {
      entry: { border: "rgba(255,255,255,0.10)", bg: "rgba(255,255,255,0.04)" },
      recommended: { border: "rgba(45,212,191,0.50)", bg: "from-teal-950/90 to-slate-900/95" },
      enterprise: { border: "rgba(255,255,255,0.10)", bg: "rgba(255,255,255,0.04)" },
    },
  },

  // ─── TIPOGRAFIA ───────────────────────────────────────────────────────────
  typography: {
    family: {
      sans: "ui-sans-serif, system-ui, sans-serif",
      mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
    },
    weight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    size: {
      xs: "0.75rem",    // 12px
      sm: "0.875rem",   // 14px
      base: "1rem",     // 16px
      lg: "1.125rem",   // 18px
      xl: "1.25rem",    // 20px
      "2xl": "1.5rem",  // 24px
      "3xl": "1.875rem",// 30px
      "4xl": "2.25rem", // 36px
      "5xl": "3rem",    // 48px
      hero: "3.15rem",  // headline principal landing
    },
    tracking: {
      tight: "-0.025em",
      normal: "0em",
      wide: "0.05em",
    },
    leading: {
      tight: "1.1",
      snug: "1.25",
      normal: "1.5",
      relaxed: "1.625",
    },
  },

  // ─── ESPAÇAMENTO E FORMA ──────────────────────────────────────────────────
  radius: {
    sm: "6px",     // botões pequenos
    md: "8px",
    lg: "10px",    // --radius padrão shadcn
    xl: "14px",    // cards principais
    "2xl": "16px",
    "3xl": "24px",
    full: "9999px",// tags, badges, avatares
  },

  // ─── SOMBRAS ──────────────────────────────────────────────────────────────
  shadow: {
    glow: "0 10px 36px -10px rgba(20,184,166,0.5), 0 0 0 0 rgba(6,182,212,0.2)",
    glowHover: "0 14px 44px -8px rgba(20,184,166,0.6), 0 0 24px 0 rgba(6,182,212,0.28)",
    card: "0 0 0 1px rgba(255,255,255,0.08)",
    tealLg: "0 20px 60px -20px rgba(20,184,166,0.35)",
  },

  // ─── ANIMAÇÕES ────────────────────────────────────────────────────────────
  motion: {
    duration: {
      fast: "0.15s",
      base: "0.28s",
      slow: "0.45s",
      enter: "0.75s",
    },
    easing: {
      out: "cubic-bezier(0,0,0.2,1)",
      inOut: "cubic-bezier(0.4,0,0.2,1)",
      spring: "cubic-bezier(0.34,1.56,0.64,1)",
    },
  },

  // ─── ÍCONE E LOGO ─────────────────────────────────────────────────────────
  logo: {
    // Ícone: Brain (lucide-react) dentro de um quadrado com gradiente primary
    iconBg: "linear-gradient(135deg, #2dd4bf 0%, #0891b2 100%)",
    iconShadow: "0 8px 24px -6px rgba(20,184,166,0.4)",
    iconRadius: "0.75rem",   // rounded-xl
    iconSize: { sm: 36, md: 40, lg: 48 }, // px
  },

  // ─── VOZ E TOM ────────────────────────────────────────────────────────────
  voice: {
    style: "Técnico mas acessível. Honesto sobre o que é MVP e o que é roadmap.",
    dos: [
      "Afirmar o que o produto faz agora",
      "Separar claramente aprendizado de avaliação formal",
      "Usar linguagem de decisão baseada em dados",
      "Nomear os papéis explicitamente (estudante, professor, coordenador)",
    ],
    donts: [
      "Prometer features que ainda são roadmap como disponíveis",
      "Misturar gamificação com nota formal",
      "Usar jargões de IA sem contexto ('revolucionário', 'disruptivo')",
      "Falar de 'IA caixa-preta' — o núcleo é determinístico",
    ],
  },
} as const;

export type BrandColor = typeof brand.color;
export type BrandTypography = typeof brand.typography;
